import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Sunrise, Sun, Sunset, Moon, CloudMoon, Clock } from "lucide-react";
import PrayerAnnouncement from "@/components/PrayerAnnouncement";

const PRAYERS = [
  { key: "fajr", label: "Fajr", Icon: CloudMoon },
  { key: "sunrise", label: "Sunrise", Icon: Sunrise, noIqamah: true },
  { key: "dhuhr", label: "Dhuhr", Icon: Sun },
  { key: "asr", label: "Asr", Icon: Sun },
  { key: "maghrib", label: "Maghrib", Icon: Sunset },
  { key: "isha", label: "Isha", Icon: Moon },
];

function parseTimeToDate(timeStr, base = new Date()) {
  if (!timeStr) return null;
  const clean = timeStr.split(" ")[0];
  const [hh, mm] = clean.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const d = new Date(base);
  d.setHours(hh, mm, 0, 0);
  return d;
}

function format12(timeStr) {
  if (!timeStr) return "—";
  const clean = timeStr.split(" ")[0];
  const [hh, mm] = clean.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(hh)) return timeStr;
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

// Hard-coded last-resort fallback so the widget can ALWAYS render. Approximate
// Flint, MI times — only used when both the API and localStorage are empty
// (first-ever visit on a brand-new browser with no network).
const HARDCODED_FALLBACK = {
  date: { readable: new Date().toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) },
  athan: { fajr: "05:30", sunrise: "06:45", dhuhr: "13:30", asr: "17:15", maghrib: "20:15", isha: "21:45" },
  iqamah: { fajr_iqamah: "06:00", dhuhr_iqamah: "13:45", asr_iqamah: "17:30", maghrib_iqamah: "20:20", isha_iqamah: "22:00", jumuah_iqamah: "13:30" },
  announcement: null,
  announcements: [],
  _fallback: true,
};

export default function PrayerTimesWidget({ compact = false }) {
  const [data, setData] = useState(() => {
    // Hydrate from localStorage immediately so visitors see SOMETHING
    // even before the API responds (e.g. on slow mobile networks).
    try {
      const cached = localStorage.getItem("fic_prayer_cache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.athan) return parsed;
      }
    } catch (_) {}
    return null;
  });
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    let cancelled = false;

    const tryFetch = async () => {
      try {
        // Cache-bust query param so neither Cloudflare nor browser ever
        // hands us a stale response. Critical for prayer times.
        const r = await api.get(`/prayer-times?_=${Date.now()}`, { timeout: 20000 });
        if (cancelled) return;
        setData(r.data);
        try { localStorage.setItem("fic_prayer_cache", JSON.stringify(r.data)); } catch (_) {}
      } catch (e) {
        // Schedule a quiet retry — never give up, never show "unavailable".
        // 30 s gap so we don't hammer a degraded backend.
        if (!cancelled) setTimeout(tryFetch, 30000);
      }
    };
    tryFetch();
    // Refresh every 15 min in the background — keeps stale localStorage fresh
    const refresh = setInterval(tryFetch, 15 * 60 * 1000);
    const tick = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => { cancelled = true; clearInterval(refresh); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ALWAYS render — if no data yet (first visit, no cache, network slow),
  // use the hardcoded fallback so the widget is never empty.
  const display = data || HARDCODED_FALLBACK;

  const { nextLabel, countdown } = useMemo(() => {
    if (!display?.athan) return { nextLabel: "", countdown: "" };
    const order = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
    const today = new Date();
    let nextKey = null;
    let nextDate = null;
    for (const k of order) {
      const d = parseTimeToDate(display.athan[k], today);
      if (d && d > now) { nextKey = k; nextDate = d; break; }
    }
    if (!nextKey) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      nextKey = "fajr";
      nextDate = parseTimeToDate(display.athan.fajr, tomorrow);
    }
    if (!nextDate) return { nextLabel: "", countdown: "" };
    const diff = nextDate - now;
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const labelMap = { fajr: "Fajr", dhuhr: "Dhuhr", asr: "Asr", maghrib: "Maghrib", isha: "Isha" };
    return { nextLabel: labelMap[nextKey], countdown: `${h}h ${m}m` };
  }, [display, now]);

  return (
    <div
      className="relative rounded-3xl overflow-hidden bg-navy text-cream shadow-2xl shadow-navy/20"
      data-testid="prayer-times-widget"
    >
      <div className="absolute inset-0 pattern-girih opacity-[0.35]" />
      <div className="relative p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Flint Islamic Center · Swartz Creek, MI</div>
            <h3 className="font-serif text-2xl md:text-3xl mt-1.5 text-cream">Today's Prayer Times</h3>
            {display?.date?.readable && (
              <div className="text-xs text-cream/65 mt-1">
                {display.date.readable} {display.date.hijri?.date ? `· ${display.date.hijri.date} ${display.date.hijri?.month?.en} ${display.date.hijri?.year}` : ""}
              </div>
            )}
          </div>
          {nextLabel && (
            <div className="rounded-2xl bg-cream/10 backdrop-blur border border-cream/15 px-5 py-3" data-testid="prayer-next">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-gold">
                <span className="pulse-dot" /> Next Prayer
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <div className="font-serif text-2xl md:text-3xl">{nextLabel}</div>
                <div className="text-sm text-cream/80 flex items-center gap-1.5"><Clock size={12} /> in {countdown}</div>
              </div>
            </div>
          )}
        </div>

        {(display.announcements?.length > 0 || display.announcement?.enabled) && (
          <PrayerAnnouncement items={display.announcements} data={display.announcement} />
        )}

        <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5">
          {PRAYERS.map(({ key, label, Icon, noIqamah }) => {
            const active = nextLabel === label;
            return (
              <div
                key={key}
                className={`rounded-2xl bg-cream/5 backdrop-blur border border-cream/10 p-3.5 transition-all hover:bg-cream/10 flex flex-col ${
                  active ? "ring-2 ring-gold/70 bg-cream/10" : ""
                }`}
                data-testid={`prayer-${key}`}
              >
                <div className="flex items-center justify-between text-gold">
                  <Icon size={16} />
                  <div className="text-[10px] uppercase tracking-[0.2em]">{label}</div>
                </div>
                <div className="mt-3">
                  <div className="text-[9px] uppercase tracking-wider text-cream/55 mb-0.5">Athan</div>
                  <div className="font-serif text-lg md:text-xl text-cream whitespace-nowrap">{format12(display.athan[key])}</div>
                </div>
                {!noIqamah && display.iqamah?.[key] && (
                  <div className="mt-2 pt-2 border-t border-cream/10">
                    <div className="text-[9px] uppercase tracking-wider text-cream/55 mb-0.5">Iqamah</div>
                    <div className="text-xs md:text-sm text-gold font-medium leading-tight">{display.iqamah[key]}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-2.5">
          <div className="rounded-2xl bg-gold/15 border border-gold/30 px-5 py-3.5 flex items-center justify-between" data-testid="jumuah-info">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Jumu'ah Khutbah</div>
              <div className="font-serif text-xl mt-0.5">{display.iqamah?.jumuah_khutbah || "2:00 PM"}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Iqamah</div>
              <div className="font-serif text-xl mt-0.5">{display.iqamah?.jumuah_iqamah || "2:30 PM"}</div>
            </div>
          </div>
          <a
            href="https://timing.athanplus.com/masjid/widgets/monthly?masjid_id=E5Avv2AX&theme=3"
            target="_blank"
            rel="noreferrer"
            className="rounded-2xl bg-cream/5 border border-cream/15 px-5 py-3.5 flex items-center justify-between hover:bg-cream/10 transition-colors group"
            data-testid="view-monthly"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Monthly Timetable</div>
              <div className="font-serif text-xl mt-0.5">View full month</div>
            </div>
            <div className="text-gold text-2xl group-hover:translate-x-1 transition-transform">→</div>
          </a>
        </div>
      </div>
    </div>
  );
}
